import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

/**
 * Template10 Layout: [LeftFrame][DoorSection][RightFrame]
 *                              [TransomTop]
 *                              [DoorMain]
 */

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const doorRightFrame = getParam(model.parameters!, 'door_right_frame_profile')
  const doorLeftFrame = getParam(model.parameters!, 'door_left_frame_profile')
  if (!leftFrame || !rightFrame || !doorRightFrame || !doorLeftFrame) return model

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const newRightFrameX = numValue - rightFrame.width
    const newDoorRightFrameX = newRightFrameX - doorRightFrame.width
    updateParam(model, 'right_frame_profile', {
      position: { ...rightFrame.position, x: newRightFrameX },
    })
    updateParam(model, 'door_right_frame_profile', {
      position: { ...doorRightFrame.position, x: newDoorRightFrameX },
    })

    const availableWidth = numValue - (leftFrame.width + rightFrame.width)
    const newDoorWidth = Math.max(600, availableWidth)
    const newDoorGlassWidth = Math.max(
      600,
      availableWidth - doorRightFrame.width - doorLeftFrame.width,
    )

    updateParam(model, 'door_top_glass_panel', { width: newDoorWidth })
    updateParam(model, 'door_glass_panel', { width: newDoorGlassWidth })
    updateParam(model, 'middle_horizontal_frame_profile_1', { width: newDoorWidth })
    updateParam(model, 'door_bottom_frame_profile', { width: newDoorWidth })
    updateParam(model, 'door_top_frame_profile', { width: newDoorWidth })

    model.doorConfiguration = {
      ...model.doorConfiguration!,
      totalDoorWidth: numValue,
      individualDoorWidth: [numValue],
    }
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates && topFrame) {
    const frameHeight = topFrame.height
    const doorTop = getParam(model.parameters!, 'door_top_glass_panel')
    const doorMain = getParam(model.parameters!, 'door_glass_panel')
    const middleHorizontal = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')

    if (doorTop && doorMain && middleHorizontal) {
      const availableHeight = numValue - frameHeight * 2
      const newDoorMainHeight = Math.max(
        800,
        availableHeight - doorTop.height - topFrame.height - middleHorizontal.height,
      )
      const newDoorMainFrameHeight = Math.max(800, availableHeight - doorTop.height)

      updateParam(model, 'door_glass_panel', { height: newDoorMainHeight })
      updateParam(model, 'door_right_frame_profile', { height: newDoorMainFrameHeight })
      updateParam(model, 'door_left_frame_profile', { height: newDoorMainFrameHeight })

      const centerFrameHeight = newDoorMainFrameHeight

      // Update handle positions (53% of door frame height)
      const doorPanel = getParam(model.parameters!, 'door_glass_panel')
      if (doorPanel?.hardware?.handles) {
        const newHandleY = Math.round(centerFrameHeight * 0.53)
        const updatedHandles = doorPanel.hardware.handles.map((handle) => ({
          ...handle,
          position: { ...handle.position, y: newHandleY },
        }))
        updateParam(model, 'door_glass_panel', {
          hardware: { ...doorPanel.hardware, handles: updatedHandles },
        })
      }

      // Adjust hinge positions
      const doorRightFrame = getParam(model.parameters!, 'door_right_frame_profile')
      if (doorRightFrame?.hardware?.hinges) {
        const oldFrameHeight = doorRightFrame.height || centerFrameHeight
        const updatedHinges = doorRightFrame.hardware.hinges.map((hinge) => {
          const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
          const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
          const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
          let newY: number
          if (isBottomHinge1) {
            newY = Math.round(centerFrameHeight * 0.75)
          } else if (isBottomHinge2) {
            newY = Math.round(centerFrameHeight * 0.85)
          } else {
            newY = Math.max(
              60,
              Math.min(
                centerFrameHeight - (hinge.height || 100) - 60,
                relativePosition * centerFrameHeight,
              ),
            )
          }
          return { ...hinge, position: { ...hinge.position, y: newY } }
        })
        updateParam(model, 'door_right_frame_profile', {
          hardware: { ...doorRightFrame.hardware, hinges: updatedHinges },
        })
      }

      // Update bottom frame Y position
      const bottomY = numValue - frameHeight
      const bottomCenter = getParam(model.parameters!, 'door_bottom_frame_profile')
      if (bottomCenter) {
        updateParam(model, 'door_bottom_frame_profile', {
          position: { ...bottomCenter.position, y: bottomY },
        })
      }
    }
  }

  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorTop = getParam(model.parameters!, 'door_top_glass_panel')
  const doorMain = getParam(model.parameters!, 'door_glass_panel')
  const middleHorizontal = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const doorLeftFrame = getParam(model.parameters!, 'door_left_frame_profile')
  const doorRightFrame = getParam(model.parameters!, 'door_right_frame_profile')
  const doorTopFrame = getParam(model.parameters!, 'door_top_frame_profile')
  const doorBottomFrame = getParam(model.parameters!, 'door_bottom_frame_profile')

  if (!doorTop || !doorMain || !middleHorizontal || !leftFrame || !topFrame) return model

  updateParam(model, 'door_top_glass_panel', {
    height: numValue - middleHorizontal.height - topFrame.height,
  })

  const newDoorMainY = topFrame.height + numValue
  updateParam(model, 'door_glass_panel', {
    height:
      leftFrame.height - numValue - (doorTopFrame?.height || 0) - (doorBottomFrame?.height || 0),
    position: { ...doorMain.position, y: newDoorMainY },
  })

  updateParam(model, 'middle_horizontal_frame_profile_1', {
    position: { ...middleHorizontal.position, y: numValue - middleHorizontal.height },
  })

  if (doorTopFrame) {
    updateParam(model, 'door_top_frame_profile', {
      position: { ...doorTopFrame.position, y: numValue },
    })
  }
  if (doorLeftFrame) {
    updateParam(model, 'door_left_frame_profile', {
      height: leftFrame.height - numValue,
      position: { ...doorLeftFrame.position, y: numValue },
    })
  }
  if (doorRightFrame) {
    updateParam(model, 'door_right_frame_profile', {
      height: leftFrame.height - numValue,
      position: { ...doorRightFrame.position, y: numValue },
    })
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorMain = getParam(model.parameters!, 'door_glass_panel')
  const doorTop = getParam(model.parameters!, 'door_top_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const middleHorizontal = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')
  const doorLeftFrame = getParam(model.parameters!, 'door_left_frame_profile')
  const doorRightFrame = getParam(model.parameters!, 'door_right_frame_profile')
  const doorBottomFrame = getParam(model.parameters!, 'door_bottom_frame_profile')
  const doorTopFrame = getParam(model.parameters!, 'door_top_frame_profile')

  if (!doorMain || !doorTop || !topFrame || !leftFrame || !middleHorizontal) return model

  const heightDifference =
    leftFrame.height - numValue - topFrame.height - middleHorizontal.height

  updateParam(model, 'door_glass_panel', {
    height: numValue - (doorTopFrame?.height || 0) - (doorBottomFrame?.height || 0),
    position: {
      ...doorMain.position,
      y:
        heightDifference +
        topFrame.height +
        middleHorizontal.height +
        (doorTopFrame?.height || 0),
    },
  })

  updateParam(model, 'door_top_glass_panel', { height: heightDifference })

  if (doorRightFrame) {
    updateParam(model, 'door_right_frame_profile', {
      height: numValue,
      position: {
        ...doorRightFrame.position,
        y: heightDifference + topFrame.height + (doorTopFrame?.height || 0),
      },
    })
  }
  if (doorLeftFrame) {
    updateParam(model, 'door_left_frame_profile', {
      height: numValue,
      position: {
        ...doorLeftFrame.position,
        y: heightDifference + topFrame.height + (doorTopFrame?.height || 0),
      },
    })
  }
  if (doorTopFrame) {
    updateParam(model, 'door_top_frame_profile', {
      position: {
        ...doorTopFrame.position,
        y: heightDifference + topFrame.height + (doorTopFrame?.height || 0),
      },
    })
  }

  updateParam(model, 'middle_horizontal_frame_profile_1', {
    position: {
      ...middleHorizontal.position,
      y: heightDifference + topFrame.height,
    },
  })

  return model
}

export const model10DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  doorPanelHeight,
}
